import { IonIcon } from '@ionic/react';
import {
  add,
  addCircle,
  addCircleOutline,
  alertCircle,
  arrowBack,
  arrowForward,
  calendarOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkDoneOutline,
  chevronBack,
  chevronDown,
  chevronForward,
  chevronUp,
  close,
  closeCircle,
  cloudOfflineOutline,
  cloudUpload,
  cloudUploadOutline,
  constructOutline,
  createOutline,
  documentAttachOutline,
  documentTextOutline,
  downloadOutline,
  eyeOffOutline,
  eyeOutline,
  gridOutline,
  helpCircleOutline,
  home,
  homeOutline,
  informationCircleOutline,
  layers,
  layersOutline,
  leafOutline,
  list,
  listOutline,
  locationOutline,
  logOutOutline,
  personOutline,
  radioButtonOff,
  radioButtonOn,
  refreshOutline,
  syncOutline,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import type { CSSProperties } from 'react';

/**
 * The design system's fixed glyph vocabulary (readme, "Iconography"). Importing only these keeps
 * the bundle small; add a name here before using it, never improvise a synonym.
 */
const GLYPHS = {
  add,
  'add-circle': addCircle,
  'add-circle-outline': addCircleOutline,
  'alert-circle': alertCircle,
  'arrow-back': arrowBack,
  'arrow-forward': arrowForward,
  'calendar-outline': calendarOutline,
  checkmark,
  'checkmark-circle': checkmarkCircle,
  'checkmark-circle-outline': checkmarkCircleOutline,
  'checkmark-done-outline': checkmarkDoneOutline,
  'chevron-back': chevronBack,
  'chevron-down': chevronDown,
  'chevron-forward': chevronForward,
  'chevron-up': chevronUp,
  close,
  'close-circle': closeCircle,
  'cloud-offline-outline': cloudOfflineOutline,
  'cloud-upload': cloudUpload,
  'cloud-upload-outline': cloudUploadOutline,
  'construct-outline': constructOutline,
  'create-outline': createOutline,
  'document-attach-outline': documentAttachOutline,
  'document-text-outline': documentTextOutline,
  'download-outline': downloadOutline,
  'eye-off-outline': eyeOffOutline,
  'eye-outline': eyeOutline,
  'grid-outline': gridOutline,
  home,
  'home-outline': homeOutline,
  'information-circle-outline': informationCircleOutline,
  layers,
  'layers-outline': layersOutline,
  'leaf-outline': leafOutline,
  list,
  'list-outline': listOutline,
  'location-outline': locationOutline,
  'log-out-outline': logOutOutline,
  'person-outline': personOutline,
  'radio-button-off': radioButtonOff,
  'radio-button-on': radioButtonOn,
  'refresh-outline': refreshOutline,
  'sync-outline': syncOutline,
  'time-outline': timeOutline,
  'trash-outline': trashOutline,
} as const;

export type IconName = keyof typeof GLYPHS;

export interface IconProps {
  name: IconName;
  size?: number | string;
  color?: string;
  /** Accessible label. Omit for decorative icons; they are hidden from assistive tech. */
  label?: string;
  style?: CSSProperties;
  className?: string;
}

/** The only way icons enter the UI. Never a hand-drawn SVG, never a Unicode glyph. */
export function Icon({
  name,
  size = 24,
  color = 'currentColor',
  label,
  style,
  className,
}: IconProps) {
  return (
    <IonIcon
      icon={GLYPHS[name] ?? helpCircleOutline}
      aria-hidden={label ? undefined : 'true'}
      aria-label={label}
      className={className}
      style={{
        fontSize: typeof size === 'number' ? `${size}px` : size,
        color,
        flex: 'none',
        display: 'inline-flex',
        ...style,
      }}
    />
  );
}
